/**
 * This file is for storing graphql queries. Split this file if it gets too large
 */

import { gql } from '@apollo/client'

export const GET_USER = gql`
  query user($id: String) {
    user(id: $id) {
      id
      account
      displayName
      nickname
      ybbAddress
      flags
      withdrawalPasswordEnabled
      firstname
      lastname
      rejectKyc1Reason
      phone
      area
    }
  }
`
/**
 * GraphQL query to fetch wallet information.
 */
export const GET_WALLETS = gql`
  query getWallets {
    getWallets {
      balance {
        value
        kind
      }
      bonusBalance {
        value
        kind
      }
      lockedBalance {
        value
        kind
      }
      frozenBalance {
        value
        kind
      }
    }
  }
`
export const USER_WALLET_EVENTS_CONNECTION = gql`
  query userWalletEventsConnection(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $systemId: String
    $orderId: String
    $partnerName: String
    $party: String
    $partyPartnerName: String
    $partyPhone: String
    $phone: String
    $address: String
    $correlationId: String
    $eventKind: [Int!]
    $currencyKind: String
    $isPayIn: Boolean
    $timeRange: [DateTimeUtc!]
    $amountRange: [String!]
    $status: Int
    $suggestedAmountRange: [String!]
  ) {
    userWalletEventsConnection(
      first: $first
      after: $after
      last: $last
      before: $before
      systemId: $systemId
      orderId: $orderId
      partnerName: $partnerName
      party: $party
      partyPartnerName: $partyPartnerName
      partyPhone: $partyPhone
      phone: $phone
      address: $address
      correlationId: $correlationId
      eventKind: $eventKind
      currencyKind: $currencyKind
      isPayIn: $isPayIn
      timeRange: $timeRange
      amountRange: $amountRange
      status: $status
      suggestedAmountRange: $suggestedAmountRange
    ) {
      pageInfo {
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
      }
      edges {
        node {
          eventNumber
          internalKey
          aggregateId
          kind
          when
          amount {
            value
            kind
          }
          correlationId
          subtotal
          fee
          metadata
          metadataExt
        }
        cursor
      }
    }
  }
`
export const GET_WALLET_EVENT_BY_NUMBER = gql`
  query getWalletEventByNumber($number: String!) {
    getWalletEventByNumber(number: $number) {
      eventNumber
      internalKey
      aggregateId
      kind
      when
      amount {
        value
        kind
      }
      correlationId
      subtotal
      fee
      metadata
      metadataExt
      receiverYbbAddress
    }
  }
`
export const CRYPTO_ADDRESS_QUERY = gql`
  query cryptoAddress {
    cryptoAddress {
      network
      address
    }
  }
`
/**
 * GraphQL query to fetch wallet information.
 */
export const GET_WALLET_BY_CURRENCY = gql`
  query getWalletByCurrency($currencyKind: CurrencyKind!) {
    getWalletByCurrency(currencyKind: $currencyKind) {
      balance {
        value
        kind
      }
      bonusBalance {
        value
        kind
      }
      lockedBalance {
        value
        kind
      }
      frozenBalance {
        value
        kind
      }
    }
  }
`

/**
 * GraphQL query to fetch user buy orders.
 * @param status - Array of order statuses.
 */
export const GET_USER_BUY_ORDER = gql`
  query GetUserBuyOrder($status: [Int!]!) {
    userGetBuyOrderByStatus(status: $status) {
      id
      internalKey
      status
      dealAmount {
        value
        kind
      }
      basePrice
      createdAt
    }
  }
`

/**
 * GraphQL query to fetch user sell orders.
 * @param status - Array of order statuses.
 */
export const USER_GET_SELL_ORDER_BY_STATUS = gql`
  query GetUserSellOrder($status: [Int!]!) {
    userGetSellOrderByStatus(status: $status) {
      id
      internalKey
      status
      actualDealAmount {
        value
        kind
      }
      expectDealAmount {
        value
        kind
      }
      basePrice
      createdAt
      pairBuyOrder {
        id
        internalKey
        status
        dealAmount {
          value
          kind
        }
        basePrice
        createdAt
      }
    }
  }
`

/**
 * GraphQL query to fetch buy order details.
 * @param internalKey - Internal key of the buy order.
 */
export const USER_GET_BUY_ORDER_BY_INTERNAL_KEY = gql`
  query userGetBuyOrderByInternalKey($internalKey: String!) {
    userGetBuyOrderByInternalKey(internalKey: $internalKey) {
      id
      status
      buyer
      seller
      dealAmount {
        value
        kind
      }
      originDealAmount {
        value
        kind
      }
      createdAt
      pairedAt
      paymentMadeAt
      finishedAt
      paymentProofUrl
      internalKey
      pairSellOrder {
        id
      }
      changeAmountProofBucketPath
      sellOrderPaymentMethod {
        bank {
          name
          bankName
          branchName
          account
        }
        alipay {
          name
          account
          url
        }
        wechat {
          name
          account
          url
        }
        ermb {
          name
          account
        }
        qq {
          name
          account
          url
        }
      }
      basePrice
      paymentMethod {
        bank {
          name
          bankName
          branchName
          account
        }
        alipay {
          name
          account
          url
        }
        wechat {
          name
          account
          url
        }
        ermb {
          name
          account
        }
        qq {
          name
          account
          url
        }
      }
    }
  }
`

/**
 * GraphQL query to fetch sell order details.
 * @param internalKey - Internal key of the sell order.
 */
export const USER_GET_SELL_ORDER_BY_INTERNAL_KEY = gql`
  query userGetSellOrderByInternalKey($internalKey: String!) {
    userGetSellOrderByInternalKey(internalKey: $internalKey) {
      id
      status
      seller
      minDealAmount {
        value
        kind
      }
      expectDealAmount {
        value
        kind
      }
      actualDealAmount {
        value
        kind
      }
      processingDealAmount {
        value
        kind
      }
      createdAt
      finishedAt
      internalKey
      pairBuyOrder {
        id
        status
        buyer
        seller
        dealAmount {
          value
          kind
        }
        originDealAmount {
          value
          kind
        }
        changeAmountProofBucketPath
        createdAt
        pairedAt
        paymentMadeAt
        finishedAt
        paymentProofUrl
        internalKey
        basePrice
        paymentMethod {
          bank {
            name
            bankName
            branchName
            account
          }
          alipay {
            name
            account
            url
          }
          wechat {
            name
            account
            url
          }
          ermb {
            name
            account
          }
          qq {
            name
            account
            url
          }
        }
      }
      basePrice
      paymentMethod {
        bank {
          name
          bankName
          branchName
          account
        }
        alipay {
          name
          account
          url
        }
        wechat {
          name
          account
          url
        }
        ermb {
          name
          account
        }
        qq {
          name
          account
          url
        }
      }
    }
  }
`
export const GET_USER_PAYMENT_METHODS = gql`
  query getUserPaymentMethods {
    getUserPaymentMethods {
      id
      kind
      info {
        bank {
          name
          bankName
          branchName
          account
        }
        alipay {
          name
          account
          url
        }
        wechat {
          name
          account
          url
        }
        ermb {
          name
          account
        }
        qq {
          name
          account
          url
        }
      }
      version
    }
  }
`
export const GET_BUY_ORDER_CONNECTION = gql`
  query getBuyOrderConnection(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $buyerId: String
    $seller: String
    $pairSellOrderId: Uuid
    $buyOrderId: Uuid
    $timeRange: [DateTimeUtc!]
    $amountRange: [String!]
    $internalKey: String
    $status: [Int!]
  ) {
    getBuyOrderConnection(
      first: $first
      after: $after
      last: $last
      before: $before
      buyerId: $buyerId
      seller: $seller
      pairSellOrderId: $pairSellOrderId
      buyOrderId: $buyOrderId
      timeRange: $timeRange
      amountRange: $amountRange
      internalKey: $internalKey
      status: $status
    ) {
      pageInfo {
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
      }
      edges {
        node {
          id
          internalKey
          status
          dealAmount {
            value
            kind
          }
          originDealAmount {
            value
            kind
          }
          createdAt
          basePrice
        }
        cursor
      }
    }
  }
`
export const GET_SELL_ORDER_CONNECTION = gql`
  query getSellOrderConnection(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $buyer: String
    $seller: String
    $pairBuyOrderId: Uuid
    $buyOrderId: Uuid
    $timeRange: [DateTimeUtc!]
    $amountRange: [String!]
    $internalKey: String
    $status: [Int!]
  ) {
    getSellOrderConnection(
      first: $first
      after: $after
      last: $last
      before: $before
      buyer: $buyer
      seller: $seller
      pairBuyOrderId: $pairBuyOrderId
      buyOrderId: $buyOrderId
      timeRange: $timeRange
      amountRange: $amountRange
      internalKey: $internalKey
      status: $status
    ) {
      pageInfo {
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
      }
      edges {
        node {
          id
          internalKey
          status
          seller
          minDealAmount {
            value
            kind
          }
          expectDealAmount {
            value
            kind
          }
          actualDealAmount {
            value
            kind
          }
          processingDealAmount {
            value
            kind
          }
          createdAt
          finishedAt
          pairBuyOrder {
            id
            internalKey
            status
            dealAmount {
              value
              kind
            }
            originDealAmount {
              value
              kind
            }
            createdAt
            pairedAt
            finishedAt
            basePrice
          }
          basePrice
          paymentMethod {
            bank {
              name
              bankName
              branchName
              account
            }
            alipay {
              name
              account
              url
            }
            wechat {
              name
              account
              url
            }
            ermb {
              name
              account
            }
            qq {
              name
              account
              url
            }
          }
        }
        cursor
      }
    }
  }
`
export const GET_BUY_ORDER_UPLOADS_PRESIGNED = gql`
  query getBuyOrderUploadsPresigned($buyOrderId: String!, $fileName: String!) {
    getBuyOrderUploadsPresigned(buyOrderId: $buyOrderId, fileName: $fileName)
  }
`
export const GET_SELL_ORDER_CASHFLOW_PROOF_PRESIGNED = gql`
  query getSellOrderCashflowProofPresigned($sellOrderId: String!, $buyOrderId: String!, $fileName: String!) {
    getSellOrderCashflowProofPresigned(sellOrderId: $sellOrderId, buyOrderId: $buyOrderId, fileName: $fileName)
  }
`
export const GET_ORDER_APPEAL_UPLOADS_PRESIGNED = gql`
  query getOrderAppealUploadsPresigned($buyOrderId: String!, $fileName: String!, $appealKind: Int!) {
    getOrderAppealUploadsPresigned(buyOrderId: $buyOrderId, fileName: $fileName, appealKind: $appealKind)
  }
`
export const USER_KYC_LV1_VIDEO_UPLOAD_PRESIGNED_PUTS = gql`
  query userKycLv1VideoUploadPresignedPuts($fileName: String!) {
    userKycLv1VideoUploadPresignedPuts(fileName: $fileName)
  }
`
export const USER_KYC_LV1_IMAGE_UPLOAD_PRESIGNED_PUTS = gql`
  query userKycLv1ImageUploadPresignedPuts($fileName: String!) {
    userKycLv1ImageUploadPresignedPuts(fileName: $fileName)
  }
`
export const RECOMMEND_BUY_AMOUNT = gql`
  query recommendBuyAmount($amount: CurrencyInput!, $paymentMethodId: String!) {
    recommendBuyAmount(amount: $amount, paymentMethodId: $paymentMethodId) {
      amount
      paymentMethodKinds
    }
  }
`
export const GET_ASSET_REQUEST_BY_INTERNAL_KEY = gql`
  query getAssetRequestByInternalKey($internalKey: String!) {
    getAssetRequestByInternalKey(internalKey: $internalKey) {
      number
      systemId
      time
      actionFlag
      status
      userId
      managerId
      network
      accountAddr
      managerAddr
      amount {
        value
        kind
      }
      fee {
        value
        kind
      }
      txHash
      memo
      processingAt
      processedAt
      finishedAt
      version
      feeVersion
      metadataExt
      userName
      userAccount
    }
  }
`
export const USER_UNREAD_NOTIFICATION_STATUS = gql`
  query userUnreadNotificationStatus($lastId: String!) {
    userUnreadNotificationStatus(lastId: $lastId) {
      numNewUnread
      latestUnreadId
      unreadExists
      numActionableSellOrders
      numActionableBuyOrders
    }
  }
`
export const NOTIFICATION_CONNECTION = gql`
  query notificationConnection(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $timeRange: [DateTimeUtc!]
    $internalKey: String
    $status: [Int!]
    $kind: [Int!]
  ) {
    notificationConnection(
      first: $first
      after: $after
      last: $last
      before: $before
      timeRange: $timeRange
      internalKey: $internalKey
      status: $status
      kind: $kind
    ) {
      pageInfo {
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
      }
      edges {
        node {
          number
          kind
          createdAt
          readAt
          payload {
            internalKey
            amount {
              value
              kind
            }
            relatedUserId
            childInternalKey
            relatedAmount {
              value
              kind
            }
            walletEventId
            bonusActivityTitle
            bonusActivityDescription
          }
          status
        }
        cursor
      }
    }
  }
`
export const GET_EXCHANGE_RATE_CONFIG = gql`
  query getExchangeRateConfig {
    getExchangeRateConfig {
      data {
        userSetting {
          tether
        }
      }
    }
  }
`
export const GET_PAYMENT_GATEWAY_DATA_BY_INTERNAL_KEY = gql`
  query getPaymentGatewayDataByInternalKey($internalKey: String!) {
    getPaymentGatewayDataByInternalKey(internalKey: $internalKey) {
      order
      partnerId
      amount
      createdAt
      name
    }
  }
`
export const GET_USER_CONNECTED_APPS = gql`
  query getUserConnectedApps {
    getUserConnectedApps {
      id
      partnerId
      partnerCode
      partnerName
      status
      connectedAt
      lastUsedAt
    }
  }
`
export const GET_CONNECT_APP_SESSION = gql`
  query getConnectAppSession($connectId: String!) {
    getConnectAppSession(connectId: $connectId) {
      connectId
      order
      partnerId
      partnerCode
      partnerName
      state
      expiresAt
      updatedAt
    }
  }
`
export const GET_BONUS_ACTIVITY_CONNECTION = gql`
  query getBonusActivityConnection(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $activityId: String
    $timeRange: [DateTimeUtc!]
    $kind: [Int!]
  ) {
    getBonusActivityConnection(
      first: $first
      after: $after
      last: $last
      before: $before
      activityId: $activityId
      timeRange: $timeRange
      kind: $kind
    ) {
      edges {
        node {
          number
          title
          description
          kind
          picturesBucketPath
          rewardFrequencyHour
          startAt
          endedAt
          id
          rewardAmount {
            value
            kind
          }
          conditionAmount {
            value
            kind
          }
          noLimit
          issueRewardTimeKind
          targetVipLevel
        }
        cursor
      }
      pageInfo {
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
      }
    }
  }
`

export const GET_BUY_ORDER_CHANGE_AMOUNT_UPLOADS_PRESIGNED = gql`
  query getBuyOrderChangeAmountUploadsPresigned($buyOrderId: String!, $fileName: String!) {
    getBuyOrderChangeAmountUploadsPresigned(buyOrderId: $buyOrderId, fileName: $fileName)
  }
`

export const GET_CURRENT_VIP_RECORD = gql`
  query getCurrentVipRecord {
    getCurrentVipRecord {
      userId
      level
      sellOrderCount
      sellOrderAmount
      exchangeYbbAmount
      periodEndedTime
    }
  }
`

export const GET_VIP_SETTING = gql`
  query getVipSetting {
    getVipSetting {
      data {
        level
        sellOrderCount
        sellOrderAmount
        exchangeYbbAmount
      }
      version
    }
  }
`

export const GET_JBP_PAYMENT_METHOD_SETTINGS = gql`
  query getJbpPaymentMethodSettings {
    getJbpPaymentMethodSettings {
      data {
        method
        bankId
        vipLevel
        enabled
        feeMode
        transactionFeeRate
        transactionFeeFlat
        minAmount
        maxAmount
      }
      version
    }
  }
`

export const GET_JBP_DEPOSIT_INVOICES = gql`
  query jbpDepositInvoicesConnection($last: Int, $before: String) {
    jbpDepositInvoicesConnection(last: $last, before: $before) {
      edges {
        cursor
        node {
          invoiceId
          companyOrderNum
          mownecumOrderNum
          walletCreditId
          bankId
          state
          requestedAmount
          requireAmount
          paidAmount
          creditedAmount
          transactionFeeAmount
          createdAt
          closedAt
          settledAt
        }
      }
      pageInfo {
        hasPreviousPage
        startCursor
      }
    }
  }
`

export const GET_JBP_DEPOSIT_INVOICE = gql`
  query jbpDepositInvoice($invoiceId: Uuid!) {
    jbpDepositInvoice(invoiceId: $invoiceId) {
      invoiceId
      companyOrderNum
      mownecumOrderNum
      walletCreditId
      bankId
      state
      requestedAmount
      requireAmount
      paidAmount
      creditedAmount
      transactionFeeAmount
      createdAt
      closedAt
      settledAt
    }
  }
`

export const MY_PROMO_TICKETS = gql`
  query myPromoTickets($limit: Int, $offset: Int) {
    myPromoTickets(limit: $limit, offset: $offset) {
      id
      promoId
      promoCode
      promoVersion
      issuedAt
      sourceEventId
      issueKey
    }
  }
`

export const TRY_GET_PROMO_TICKET = gql`
  query tryGetPromoTicket($orderId: String!) {
    tryGetPromoTicket(orderId: $orderId) {
      id
      promoId
      promoCode
      promoVersion
      issuedAt
      sourceEventId
      issueKey
    }
  }
`

// Marketing promo for Scan2Pay QR
export const GET_SCAN2PAY_QR_PROMO = gql`
  query getScan2payQrPromo {
    getScan2payQrPromo {
      data {
        kind
        value
      }
      version
    }
  }
`
